/**
 * WitnessQueuePage — DRR-063 Tier 4 Dual Verification Queue
 *
 * Life-critical page: witnesses verify and complete high-risk operations.
 * - Tier 3 (hardware key) required to VERIFY
 * - Tier 4 required to COMPLETE
 *
 * All types defined inline — these are not yet in generated/graphql.ts
 * (schema needs rebuild after Rust gateway change). Replace with generated
 * imports after the next `pnpm codegen` run.
 *
 * Route: /admin/witness-queue
 * Guards: Backend RequireScope + RequireAuthTier on all resolvers
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Modal,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconClock,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconKey,
  IconUser,
  IconEye,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useAuth } from '@/auth/AuthContext';

// ---------------------------------------------------------------------------
// Types — inline until schema rebuild produces generated/graphql.ts entries
// ---------------------------------------------------------------------------

type WitnessStatus =
  | 'Pending'
  | 'Verified'
  | 'Completed'
  | 'Expired'
  | 'Cancelled'
  | 'Rejected';
import {
  usePendingWitnessRequestsQuery,
  useMyWitnessRequestsQuery,
  useVerifyWitnessRequestMutation,
  useCompleteWitnessRequestMutation,
  useCancelWitnessRequestMutation,
  useRejectWitnessRequestMutation,
} from '@/generated/graphql';

type WitnessOperationType =
  | 'MedicationAdministration'
  | 'BloodTransfusion'
  | 'HighRiskMedication'
  | 'DischargeAgainstAdvice'
  | 'HighValueTransaction'
  | 'WireTransferApproval'
  | 'BatchPaymentRelease'
  | 'LegalDocumentExecution'
  | 'SettlementApproval'
  | 'EmergencyOverride'
  | 'Custom';

interface WitnessRequest {
  id: string;
  tenantId: string;
  operatorId: string;
  operationType: WitnessOperationType;
  entityId: string;
  entityType: string;
  description: string;
  status: WitnessStatus;
  witnessId?: string;
  witnessRole?: string;
  createdAt: string;
  expiresAt: string;
  isExpired: boolean;
  secondsRemaining?: number;
}

// ---------------------------------------------------------------------------
// GraphQL operations
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatOperationType(raw: string): string {
  // Convert camelCase to space-separated words
  return raw.replace(/([A-Z])/g, ' $1').trim();
}

function formatCountdown(secondsRemaining: number | undefined, isExpired: boolean): string {
  if (isExpired || secondsRemaining === 0) return 'Expired';
  if (secondsRemaining === undefined) return '—';
  if (secondsRemaining < 60) return `${secondsRemaining}s`;
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  return `${minutes}m ${seconds}s`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function witnessStatusBadge(status: WitnessStatus) {
  switch (status) {
    case 'Pending':
      return <Badge color="yellow" leftSection={<IconClock size={12} />}>Pending</Badge>;
    case 'Verified':
      return <Badge color="blue" leftSection={<IconEye size={12} />}>Verified</Badge>;
    case 'Completed':
      return <Badge color="green" leftSection={<IconCheck size={12} />}>Completed</Badge>;
    case 'Expired':
      return <Badge color="gray">Expired</Badge>;
    case 'Cancelled':
      return <Badge color="gray" leftSection={<IconX size={12} />}>Cancelled</Badge>;
    case 'Rejected':
      return <Badge color="red" leftSection={<IconX size={12} />}>Rejected</Badge>;
    default:
      return <Badge color="gray">{status}</Badge>;
  }
}

// ---------------------------------------------------------------------------
// WitnessVerifyModal
// ---------------------------------------------------------------------------

interface WitnessVerifyModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly request: WitnessRequest | null;
  readonly onVerified: (completionToken: string) => void;
}

function WitnessVerifyModal({ opened, onClose, request, onVerified }: Readonly<WitnessVerifyModalProps>) {
  const [witnessRole, setWitnessRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completionToken, setCompletionToken] = useState<string | null>(null);

  const [verifyWitnessRequest] = useVerifyWitnessRequestMutation({
    refetchQueries: ['PendingWitnessRequests', 'MyWitnessRequests'],
  });

  const handleVerify = useCallback(async () => {
    if (!request) return;
    if (!witnessRole.trim()) {
      setError('Witness role is required.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data } = await verifyWitnessRequest({
        variables: {
          input: {
            requestId: request.id,
            witnessRole: witnessRole.trim(),
          },
        },
      });
      const result = data as { verifyWitnessRequest: { request: { id: string; status: string; witnessId: string; witnessRole: string }; completionToken: string } };
      const token: string = result.verifyWitnessRequest.completionToken;
      setCompletionToken(token);
      onVerified(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    } finally {
      setLoading(false);
    }
  }, [request, witnessRole, verifyWitnessRequest, onVerified]);

  const handleClose = useCallback(() => {
    setWitnessRole('');
    setError(null);
    setCompletionToken(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Verify Witness Request"
      size="md"
    >
      <Stack gap="md">
        <Alert color="orange" variant="light" icon={<IconKey size={16} />}>
          Hardware security key required. You cannot witness your own request.
        </Alert>

        {request && (
          <Card withBorder padding="sm" bg="gray.0">
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>Operation:</Text>
                <Text size="sm">{formatOperationType(request.operationType)}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={600}>Entity:</Text>
                <Text size="sm">{request.entityType} / {request.entityId}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={600}>Description:</Text>
                <Text size="sm">{request.description}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={600}>Operator:</Text>
                <Text size="sm">{request.operatorId}</Text>
              </Group>
            </Stack>
          </Card>
        )}

        {!completionToken ? (
          <>
            <TextInput
              label="Your Witness Role"
              placeholder="e.g., Charge Nurse, Supervisor, Compliance Officer"
              description="Your role in this verification — recorded in the audit trail"
              value={witnessRole}
              onChange={(e) => setWitnessRole(e.currentTarget.value)}
              required
            />

            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                {error}
              </Alert>
            )}

            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleVerify}
                loading={loading}
                disabled={!witnessRole.trim()}
                leftSection={<IconShieldCheck size={16} />}
                color="blue"
              >
                Verify (Tier 3 Required)
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Alert color="green" icon={<IconCheck size={16} />}>
              Verification recorded. Give the completion token to the operator to complete the operation.
            </Alert>

            <Stack gap="xs">
              <Text size="sm" fw={600}>Completion Token</Text>
              <Text
                size="sm"
                ff="monospace"
                style={{
                  background: 'var(--mantine-color-gray-1)',
                  padding: '8px 12px',
                  borderRadius: 4,
                  wordBreak: 'break-all',
                }}
              >
                {completionToken}
              </Text>
              <Text size="xs" c="dimmed">
                This token is required to complete the operation. It has been recorded.
              </Text>
            </Stack>

            <Group justify="flex-end">
              <Button onClick={handleClose} leftSection={<IconCheck size={16} />}>
                Done
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// WitnessCompleteModal
// ---------------------------------------------------------------------------

interface WitnessCompleteModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly request: WitnessRequest | null;
  readonly completionToken: string;
  readonly onCompleted: () => void;
}

function WitnessCompleteModal({ opened, onClose, request, completionToken, onCompleted }: Readonly<WitnessCompleteModalProps>) {
  const [tokenInput, setTokenInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [completeWitnessRequest] = useCompleteWitnessRequestMutation({
    refetchQueries: ['PendingWitnessRequests', 'MyWitnessRequests'],
  });

  const handleComplete = useCallback(async () => {
    if (!request) return;
    if (!tokenInput.trim()) {
      setError('Completion token is required.');
      return;
    }
    if (tokenInput.trim() !== completionToken) {
      setError('Token does not match. Please re-enter the token provided by the witness.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await completeWitnessRequest({
        variables: {
          input: {
            requestId: request.id,
            completionToken: tokenInput.trim(),
          },
        },
      });
      onCompleted();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Completion failed.');
    } finally {
      setLoading(false);
    }
  }, [request, tokenInput, completionToken, completeWitnessRequest, onCompleted, onClose]);

  const handleClose = useCallback(() => {
    setTokenInput('');
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Complete Witnessed Operation"
      size="md"
    >
      <Stack gap="md">
        <Alert color="red" variant="light" icon={<IconKey size={16} />}>
          Tier 4 operation — hardware security key required.
        </Alert>

        {request && (
          <Card withBorder padding="sm" bg="gray.0">
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>Operation:</Text>
                <Text size="sm">{formatOperationType(request.operationType)}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={600}>Witnessed By:</Text>
                <Text size="sm">{request.witnessId ?? '—'} ({request.witnessRole ?? '—'})</Text>
              </Group>
            </Stack>
          </Card>
        )}

        <Stack gap="xs">
          <Text size="sm" fw={500}>Completion Token (from witness)</Text>
          <Text
            size="sm"
            ff="monospace"
            c="dimmed"
            style={{
              background: 'var(--mantine-color-gray-1)',
              padding: '8px 12px',
              borderRadius: 4,
              wordBreak: 'break-all',
            }}
          >
            {completionToken || '(token provided by witness)'}
          </Text>
        </Stack>

        <TextInput
          label="Re-enter Completion Token"
          placeholder="Paste the token provided by your witness"
          description="Must match exactly to confirm the witnessed operation"
          value={tokenInput}
          onChange={(e) => setTokenInput(e.currentTarget.value)}
          ff="monospace"
          required
        />

        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleComplete}
            loading={loading}
            disabled={!tokenInput.trim()}
            leftSection={<IconKey size={16} />}
          >
            Complete Operation (Tier 4)
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// WitnessRejectModal
// ---------------------------------------------------------------------------

interface WitnessRejectModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly request: WitnessRequest | null;
  readonly onRejected: () => void;
}

function WitnessRejectModal({ opened, onClose, request, onRejected }: Readonly<WitnessRejectModalProps>) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [rejectWitnessRequest] = useRejectWitnessRequestMutation({
    refetchQueries: ['PendingWitnessRequests', 'MyWitnessRequests'],
  });

  const handleReject = useCallback(async () => {
    if (!request) return;
    if (reason.trim().length < 10) {
      setError('Reason must be at least 10 characters for the audit trail.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await rejectWitnessRequest({
        variables: {
          input: {
            requestId: request.id,
            reason: reason.trim(),
          },
        },
      });
      setReason('');
      onRejected();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rejection failed.');
    } finally {
      setLoading(false);
    }
  }, [request, reason, rejectWitnessRequest, onRejected, onClose]);

  const handleClose = useCallback(() => {
    setReason('');
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Reject Witness Request"
      size="md"
    >
      <Stack gap="md">
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
          Rejecting this request will prevent the operation from being completed.
          This action is recorded in the immutable audit trail.
        </Alert>

        {request && (
          <Card withBorder padding="sm" bg="gray.0">
            <Stack gap="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>Operation:</Text>
                <Text size="sm">{formatOperationType(request.operationType)}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={600}>Operator:</Text>
                <Text size="sm">{request.operatorId}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" fw={600}>Description:</Text>
                <Text size="sm">{request.description}</Text>
              </Group>
            </Stack>
          </Card>
        )}

        <Textarea
          label="Reason for Rejection"
          placeholder="Explain why this witness request is being rejected..."
          description="Minimum 10 characters — required for compliance audit trail"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
          required
        />

        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {error}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleReject}
            loading={loading}
            disabled={reason.trim().length < 10}
            leftSection={<IconX size={16} />}
          >
            Reject Request
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Pending Requests Tab
// ---------------------------------------------------------------------------

interface PendingRequestsTabProps {
  readonly requests: WitnessRequest[];
  readonly onVerify: (request: WitnessRequest) => void;
  readonly onReject: (request: WitnessRequest) => void;
}

function PendingRequestsTab({ requests, onVerify, onReject }: Readonly<PendingRequestsTabProps>) {
  if (requests.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Center>
          <Stack align="center" gap="sm">
            <IconShieldCheck size={48} color="gray" />
            <Text size="lg" fw={500}>No Pending Witness Requests</Text>
            <Text size="sm" c="dimmed">
              There are no operations awaiting your witness verification.
            </Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Operation Type</Table.Th>
          <Table.Th>Entity</Table.Th>
          <Table.Th>Description</Table.Th>
          <Table.Th>Operator</Table.Th>
          <Table.Th>Expires In</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {requests.map((req) => {
          const countdownText = formatCountdown(req.secondsRemaining, req.isExpired);
          const isUrgent = !req.isExpired && req.secondsRemaining !== undefined && req.secondsRemaining < 60;

          return (
            <Table.Tr key={req.id}>
              <Table.Td>
                <Text size="sm" fw={500}>{formatOperationType(req.operationType)}</Text>
              </Table.Td>
              <Table.Td>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">{req.entityType}</Text>
                  <Text size="sm" ff="monospace">{req.entityId}</Text>
                </Stack>
              </Table.Td>
              <Table.Td>
                <Text size="sm" lineClamp={2}>{req.description}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" ff="monospace">{req.operatorId}</Text>
              </Table.Td>
              <Table.Td>
                <Text
                  size="sm"
                  c={req.isExpired ? 'red' : isUrgent ? 'orange' : 'inherit'}
                  fw={isUrgent ? 600 : undefined}
                >
                  {countdownText}
                </Text>
              </Table.Td>
              <Table.Td>{witnessStatusBadge(req.status)}</Table.Td>
              <Table.Td>
                <Group gap="xs">
                  {req.status === 'Pending' && !req.isExpired && (
                    <>
                      <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        leftSection={<IconShieldCheck size={12} />}
                        onClick={() => onVerify(req)}
                      >
                        Verify
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconX size={12} />}
                        onClick={() => onReject(req)}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {(req.status !== 'Pending' || req.isExpired) && (
                    <Text size="xs" c="dimmed">—</Text>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// My Requests Tab
// ---------------------------------------------------------------------------

interface MyRequestsTabProps {
  readonly requests: WitnessRequest[];
  readonly onCancel: (request: WitnessRequest) => void;
  readonly onComplete: (request: WitnessRequest) => void;
}

function MyRequestsTab({ requests, onCancel, onComplete }: Readonly<MyRequestsTabProps>) {
  if (requests.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Center>
          <Stack align="center" gap="sm">
            <IconUser size={48} color="gray" />
            <Text size="lg" fw={500}>No Requests</Text>
            <Text size="sm" c="dimmed">
              You have not submitted any witness requests.
            </Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Operation Type</Table.Th>
          <Table.Th>Entity</Table.Th>
          <Table.Th>Description</Table.Th>
          <Table.Th>Witness</Table.Th>
          <Table.Th>Expires In</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {requests.map((req) => {
          const countdownText = formatCountdown(req.secondsRemaining, req.isExpired);
          const isUrgent = !req.isExpired && req.secondsRemaining !== undefined && req.secondsRemaining < 60;

          return (
            <Table.Tr key={req.id}>
              <Table.Td>
                <Text size="sm" fw={500}>{formatOperationType(req.operationType)}</Text>
              </Table.Td>
              <Table.Td>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">{req.entityType}</Text>
                  <Text size="sm" ff="monospace">{req.entityId}</Text>
                </Stack>
              </Table.Td>
              <Table.Td>
                <Text size="sm" lineClamp={2}>{req.description}</Text>
              </Table.Td>
              <Table.Td>
                {req.witnessId
                  ? (
                    <Stack gap={2}>
                      <Text size="sm" ff="monospace">{req.witnessId}</Text>
                      {req.witnessRole && <Text size="xs" c="dimmed">{req.witnessRole}</Text>}
                    </Stack>
                  )
                  : <Text size="sm" c="dimmed">Awaiting witness</Text>}
              </Table.Td>
              <Table.Td>
                <Text
                  size="sm"
                  c={req.isExpired ? 'red' : isUrgent ? 'orange' : 'inherit'}
                  fw={isUrgent ? 600 : undefined}
                >
                  {countdownText}
                </Text>
              </Table.Td>
              <Table.Td>{witnessStatusBadge(req.status)}</Table.Td>
              <Table.Td>
                <Group gap="xs">
                  {req.status === 'Pending' && !req.isExpired && (
                    <Button
                      size="xs"
                      variant="subtle"
                      color="orange"
                      leftSection={<IconX size={12} />}
                      onClick={() => onCancel(req)}
                    >
                      Cancel
                    </Button>
                  )}
                  {req.status === 'Verified' && (
                    <Button
                      size="xs"
                      variant="light"
                      color="green"
                      leftSection={<IconCheck size={12} />}
                      onClick={() => onComplete(req)}
                    >
                      Complete
                    </Button>
                  )}
                  {req.status !== 'Pending' && req.status !== 'Verified' && (
                    <Text size="xs" c="dimmed">—</Text>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function WitnessQueuePage() {
  const { tenant } = useAuth();

  // Verify modal
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyTarget, setVerifyTarget] = useState<WitnessRequest | null>(null);

  // Complete modal
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState<WitnessRequest | null>(null);
  const [pendingCompletionToken, setPendingCompletionToken] = useState('');

  // Reject modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<WitnessRequest | null>(null);

  const { data: pendingData, loading: pendingLoading, error: pendingError } = usePendingWitnessRequestsQuery({ fetchPolicy: 'network-only', pollInterval: 30_000 },
  );

  const { data: myData, loading: myLoading, error: myError } = useMyWitnessRequestsQuery({ fetchPolicy: 'network-only', pollInterval: 30_000 },
  );

  const [cancelWitnessRequest] = useCancelWitnessRequestMutation({
    refetchQueries: ['PendingWitnessRequests', 'MyWitnessRequests'],
  });

  const pendingRequests: WitnessRequest[] = useMemo(
    () => (pendingData as { pendingWitnessRequests: WitnessRequest[] } | undefined)?.pendingWitnessRequests ?? [],
    [pendingData],
  );

  const myRequests: WitnessRequest[] = useMemo(
    () => (myData as { myWitnessRequests: WitnessRequest[] } | undefined)?.myWitnessRequests ?? [],
    [myData],
  );

  const handleVerifyClick = useCallback((req: WitnessRequest) => {
    setVerifyTarget(req);
    setVerifyModalOpen(true);
  }, []);

  const handleVerified = useCallback((token: string) => {
    // Keep verify modal open to show the token to the witness
    // The witness relays the token to the operator out-of-band
    setPendingCompletionToken(token);
  }, []);

  const handleRejectClick = useCallback((req: WitnessRequest) => {
    setRejectTarget(req);
    setRejectModalOpen(true);
  }, []);

  const handleCompleteClick = useCallback((req: WitnessRequest) => {
    setCompleteTarget(req);
    setCompleteModalOpen(true);
  }, []);

  const handleCancelRequest = useCallback(async (req: WitnessRequest) => {
    try {
      await cancelWitnessRequest({ variables: { requestId: req.id } });
    } catch (err) {
      console.error('Cancel witness request failed:', err);
    }
  }, [cancelWitnessRequest]);

  if (!tenant) {
    return <Alert color="yellow">No tenant context. Please select a tenant.</Alert>;
  }

  const isLoading = pendingLoading || myLoading;
  const loadError = pendingError ?? myError;

  if (loadError) {
    return (
      <Alert color="red" title="Error loading witness queue" icon={<IconAlertTriangle size={16} />}>
        {loadError.message}
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="sm">
          <IconShieldCheck size={28} />
          <Title order={3}>Witness Queue</Title>
        </Group>
      </Group>

      {/* DRR-063 Tier info alert */}
      <Alert color="orange" variant="light" icon={<IconKey size={16} />}>
        Tier 3 (hardware key) required to verify. Tier 4 required to complete. DRR-063 dual verification.
      </Alert>

      {/* Stats */}
      {isLoading ? (
        <Center p="xl"><Loader /></Center>
      ) : (
        <>
          <SimpleGrid cols={2}>
            <Card withBorder padding="md" radius="md">
              <Stack gap="xs" align="center">
                <Text size="xl" fw={700} c={pendingRequests.length > 0 ? 'yellow' : 'dimmed'}>
                  {pendingRequests.length}
                </Text>
                <Text size="sm" c="dimmed">Pending Your Witness</Text>
              </Stack>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Stack gap="xs" align="center">
                <Text size="xl" fw={700} c="blue">
                  {myRequests.length}
                </Text>
                <Text size="sm" c="dimmed">My Submitted Requests</Text>
              </Stack>
            </Card>
          </SimpleGrid>

          <Tabs defaultValue="pending">
            <Tabs.List>
              <Tabs.Tab
                value="pending"
                leftSection={<IconEye size={16} />}
                rightSection={
                  pendingRequests.length > 0
                    ? <Badge size="sm" color="yellow" circle>{pendingRequests.length}</Badge>
                    : undefined
                }
              >
                Pending Requests
              </Tabs.Tab>
              <Tabs.Tab
                value="mine"
                leftSection={<IconUser size={16} />}
                rightSection={
                  myRequests.length > 0
                    ? <Badge size="sm" color="blue" circle>{myRequests.length}</Badge>
                    : undefined
                }
              >
                My Requests
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="pending" pt="md">
              <PendingRequestsTab
                requests={pendingRequests}
                onVerify={handleVerifyClick}
                onReject={handleRejectClick}
              />
            </Tabs.Panel>

            <Tabs.Panel value="mine" pt="md">
              <MyRequestsTab
                requests={myRequests}
                onCancel={handleCancelRequest}
                onComplete={handleCompleteClick}
              />
            </Tabs.Panel>
          </Tabs>
        </>
      )}

      {/* Verify Modal */}
      <WitnessVerifyModal
        opened={verifyModalOpen}
        onClose={() => {
          setVerifyModalOpen(false);
          setVerifyTarget(null);
          setPendingCompletionToken('');
        }}
        request={verifyTarget}
        onVerified={handleVerified}
      />

      {/* Complete Modal */}
      <WitnessCompleteModal
        opened={completeModalOpen}
        onClose={() => {
          setCompleteModalOpen(false);
          setCompleteTarget(null);
        }}
        request={completeTarget}
        completionToken={pendingCompletionToken}
        onCompleted={() => {
          setCompleteModalOpen(false);
          setCompleteTarget(null);
          setPendingCompletionToken('');
        }}
      />

      {/* Reject Modal */}
      <WitnessRejectModal
        opened={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setRejectTarget(null);
        }}
        request={rejectTarget}
        onRejected={() => {
          setRejectModalOpen(false);
          setRejectTarget(null);
        }}
      />
    </Stack>
  );
}

export default WitnessQueuePage;
